import { Component, DestroyRef, EnvironmentInjector, inject, Inject, PLATFORM_ID, ChangeDetectorRef, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouteConfigLoadStart, RouteConfigLoadEnd, RouterModule } from '@angular/router';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavBarComponent } from './layout/nav-bar/nav-bar.component';
import { LoaderService } from './core/services/loader.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgcCookieConsentModule, NgcCookieConsentService } from 'ngx-cookieconsent';
import { filter, map, startWith, Observable } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { AuthenticationService } from './core/services/authentication.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FooterComponent } from './layout/footer/footer.component';
import { FlashMessageComponent } from "./shared/components/flash-message/flash-message.component";
import { FeedSignalRService } from './core/services/feed-signalr.service';
import { LoginPopupComponent } from './shared/components/login-popup/login-popup.component';
import { SEOService } from './core/services/seo.service';
import { CountryByIpService } from './core/services/country-by-ip.service';
declare const gtag: Function;

@Component({
  selector: 'app-root',
  imports: [NavBarComponent, TranslateModule, NgcCookieConsentModule, CommonModule, RouterModule, FooterComponent, FlashMessageComponent, LoginPopupComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  isBrowser: boolean;
  screenHeight = '500px';
  showLoader = false;
  loadingRouteConfig = false;
  isManufacturer = false;
  hideNavbar!: Observable<boolean>;
  selectedLang: string = '';
  isMobile = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(EnvironmentInjector);

  constructor(
    private readonly router: Router,
    private readonly seoService: SEOService,
    private readonly route: ActivatedRoute,
    public loaderService: LoaderService,
    private readonly cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly titleService: Title,
    public translate: TranslateService,
    public authenticationService: AuthenticationService,
    public signalRService: FeedSignalRService,
    private readonly countryByIpService: CountryByIpService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    translate.addLangs(['it', 'en', 'es', 'de']);

    if (this.isBrowser) {
      this.signalRService.startConnection();
    }

    // Initialize hideNavbar observable safely in constructor
    this.hideNavbar = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let r = this.route;
        while (r.firstChild) r = r.firstChild;
        return !!r.snapshot.data['hideNavbar'];
      })
    );

    if (this.isBrowser) {
      this.screenHeight = `${window.innerHeight - 54}px`;

      this.selectedLang = this.translate.getCurrentLang() || 'it';

      this.document.documentElement.lang = this.selectedLang;

      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(event => {

        // GA4 tracking
        if (this.isBrowser) {
          gtag?.('event', 'page_view', {
            page_title: this.titleService.getTitle(),
            page_path: event.urlAfterRedirects,
            page_location: globalThis.location.href
          });
        }

        // SEO for SSR + browser
        let currentRoute = this.route;

        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }

        const data = currentRoute.snapshot.data;

        this.seoService.applySEO({
          title: data?.['title'],
          description: data?.['description'],
          url: 'https://skeinforce.com' + this.router.url,
          type: 'website'
        });

      });
    }

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {

        this.document.documentElement.lang = event.lang;

        if (!this.isBrowser) return;

        const ccService = this.injector.get(NgcCookieConsentService);

        const config = ccService.getConfig();
        config.content ??= {};
        config.content.message = this.translate.instant('cookie_message');
        config.content.allow = this.translate.instant('allow');
        config.content.deny = this.translate.instant('deny');

        ccService.destroy();
        ccService.init(config);
      });
  }

  ngOnInit(): void {
    this.checkDevice();
    if (this.isBrowser) {
      //this.loadGoogleAnalytics();

      setTimeout(() => {
        this.isManufacturer = globalThis.location.href.includes('/manufacturer/');
      });

      this.router.events
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          if (event instanceof RouteConfigLoadStart) {
            this.loadingRouteConfig = true;
            this.cd.detectChanges();
          } else if (event instanceof RouteConfigLoadEnd) {
            this.loadingRouteConfig = false;
            this.cd.detectChanges();
          }
        });
    }

    void this.countryByIpService.initialize();
  }

  checkDevice() {
    if (!this.isBrowser) return;

    const isSmallScreen = window.innerWidth <= 768;
    const isMobileUA = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    this.isMobile = isSmallScreen || isMobileUA;
  }

  @HostListener('window:resize')
  onResize() {
    if (!this.isBrowser) return;
    this.checkDevice();
  }

  onActivate(): void {
    if (this.isBrowser) window.scrollTo(0, 0);
  }

  private getTitle(route: ActivatedRoute): string[] {
    const data: string[] = [];
    if (route.snapshot?.data?.['title']) data.push(route.snapshot.data['title']);
    if (route.firstChild) data.push(...this.getTitle(route.firstChild));
    return data;
  }

  loadExternalScript(scriptUrl: string): Promise<void> {
    if (!this.isBrowser) return Promise.resolve();
    return new Promise(resolve => {
      const scriptElement = this.document.createElement('script');
      scriptElement.src = scriptUrl;
      scriptElement.onload = () => resolve();
      this.document.body.appendChild(scriptElement);
    });
  }
}
